/**
 * useRoadmapItemLikedUsers Hook
 *
 * State orchestration for the owner-only RoadmapItemLikesBottomSheet: the
 * initial page load, infinite-scroll pagination with fetch guards, the
 * `!hasMore` early-return, and unique-append dedup by `userId`. The `likedType`
 * flag selects the like-vs-dislike data branch.
 *
 * Ports Flutter's `RoadmapItemLikesBottomSheetState._getData` + the
 * post-frame initial load
 * (`lib/presentation/bottom_sheet/roadmap_item_likes_bottom_sheet.dart:84-120`).
 *
 * Business logic lives here, not in the owning sheet component. The hook calls
 * `fetchRoadmapItemLikedUsersUseCase` (never the service / Firestore directly).
 * Unlike the content liked-users sheet, the roadmap likes sheet applies NO
 * blocked-user filter (Task 5 / `roadmap_repository.dart:102-109`), so there is
 * no `currentUser` plumbing here.
 */

import { useState, useEffect, useRef, useCallback } from 'react';
import type { PageCursor } from '@/data/types/json';
import type { ContentLikedUserEntity } from '@/domain/entities/contentReaction/contentLikedUserEntity';
import { fetchRoadmapItemLikedUsersUseCase } from '@/domain/usecases/roadmap/fetchRoadmapItemLikedUsersUseCase';
import { appLogger } from '@/domain/services/analytics/appLogger';

export interface UseRoadmapItemLikedUsersParams {
  roadmapItemId: string;
  /** `true` → users who liked; `false` → users who disliked. */
  likedType: boolean;
}

export interface UseRoadmapItemLikedUsersResult {
  likedUsers: ContentLikedUserEntity[];
  hasMore: boolean;
  isFetching: boolean;
  /** True until the first fetch resolves; the sheet shows a centered loader. */
  isFirstLoad: boolean;
  loadMore: () => void;
}

export function useRoadmapItemLikedUsers({
  roadmapItemId,
  likedType,
}: UseRoadmapItemLikedUsersParams): UseRoadmapItemLikedUsersResult {
  const [likedUsers, setLikedUsers] = useState<ContentLikedUserEntity[]>([]);
  const [hasMore, setHasMore] = useState(true);
  const [isFetching, setIsFetching] = useState(false);
  const [isFirstLoad, setIsFirstLoad] = useState(true);

  // Refs mirror the synchronous Flutter fields (`_lastFetchedItem`, `_hasMore`).
  // React state updates are async, so the fetch reads the refs to make its
  // guards and cursor handling reliable across rapid pagination calls.
  const cursorRef = useRef<PageCursor | undefined>(undefined);
  const hasMoreRef = useRef(true);
  const isFetchingRef = useRef(false);

  // Ports Flutter `_getData` (roadmap_item_likes_bottom_sheet.dart:93-120).
  const loadMore = useCallback(async () => {
    // Early-return on `!_hasMore` (line 94 — the sheet's sole guard). The
    // `isFetching` guard is the web equivalent of Flutter's
    // `_dataFetchNotifier.setFetching` in-flight gate (lines 97-99) — it
    // prevents overlapping pages on rapid scroll.
    if (!hasMoreRef.current || isFetchingRef.current) {
      return;
    }

    isFetchingRef.current = true;
    setIsFetching(true);

    try {
      const result = await fetchRoadmapItemLikedUsersUseCase.execute({
        roadmapItemId,
        liked: likedType,
        cursor: cursorRef.current,
      });

      // Port the `_items.add(...)` append loop (lines 105-112) with dedup by
      // `userId` — newest-first order is preserved by appending the new page
      // after the existing rows.
      setLikedUsers((prev) => {
        const existingIds = new Set(prev.map((row) => row.userId));
        const additions = result.likedUsers.filter(
          (row) => !existingIds.has(row.userId),
        );
        return additions.length > 0 ? [...prev, ...additions] : prev;
      });

      cursorRef.current = result.cursor;
      hasMoreRef.current = result.hasMore;
      setHasMore(result.hasMore);
    } catch (error) {
      appLogger.error(
        'Error fetching roadmap item liked users',
        'useRoadmapItemLikedUsers.loadMore',
        { error: error as Error },
      );
    } finally {
      isFetchingRef.current = false;
      setIsFetching(false);
      setIsFirstLoad(false);
    }
  }, [roadmapItemId, likedType]);

  // Initial load once on mount, mirroring Flutter's post-frame `_getData`
  // (roadmap_item_likes_bottom_sheet.dart:84-86).
  useEffect(() => {
    void loadMore();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return {
    likedUsers,
    hasMore,
    isFetching,
    isFirstLoad,
    loadMore,
  };
}
